'use client';

import type { PageHeaderProps } from '@routeroyal/theme-core';
import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';

/**
 * AllysPageHeader - Allys-themed header section for static pages
 * 
 * Displays the page title, subtitle, excerpt, and optional featured image
 * with Allys navy/gold styling and smooth animations.
 */
export function AllysPageHeader({
  title,
  subtitle,
  excerpt,
  featuredImage,
  showBreadcrumb = false,
  breadcrumbItems = [],
  className = '',
}: PageHeaderProps) {
  return (
    <motion.header
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`relative [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}
    >
      {/* Breadcrumb Navigation */}
      {showBreadcrumb && breadcrumbItems.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-gray-500">
            {breadcrumbItems.map((item, index) => (
              <li key={index} className="flex items-center gap-2">
                {index > 0 && (
                  <svg
                    className="h-4 w-4 text-[#BBA339]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                )}
                {item.href ? (
                  <Link
                    href={item.href}
                    className="text-[#243462] hover:text-[#BBA339] transition-colors duration-300 font-medium"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-gray-500">{item.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Featured Image */}
      {featuredImage && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative w-full aspect-[21/9] mb-10 rounded-2xl overflow-hidden ring-1 ring-gray-200"
        >
          <Image
            src={featuredImage.url}
            alt={featuredImage.alt || title}
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#243462]/60 via-transparent to-transparent" />
          {featuredImage.caption && (
            <div className="absolute bottom-0 left-0 right-0 text-white text-sm p-4 pt-8">
              {featuredImage.caption}
            </div>
          )}
        </motion.div>
      )}

      {/* Title */}
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight text-[#243462]">
        {title}
      </h1>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-lg text-[#BBA339] font-medium mb-4">
          {subtitle}
        </p>
      )}

      {/* Excerpt */}
      {excerpt && (
        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
          {excerpt}
        </p>
      )}
    </motion.header>
  );
}
